import type { VideoDailyMetric } from '@/modules/videos/types'

export type MetricFormat = 'currency' | 'number' | 'percent'
export type MetricAggregation = 'sum' | 'average' | 'ratio'

export interface VideoMetricDefinition {
  key: keyof Omit<VideoDailyMetric, 'clientId' | 'date'>
  label: string
  format: MetricFormat
  aggregation: MetricAggregation
  /** For 'ratio' metrics: headline = sum(numerator) / sum(denominator). */
  ratio?: {
    numerator: keyof Omit<VideoDailyMetric, 'clientId' | 'date'>
    denominator: keyof Omit<VideoDailyMetric, 'clientId' | 'date'>
  }
  /** True when the underlying field is not yet ingested. */
  isDemo?: boolean
}

export const VIDEO_METRIC_CATALOG: VideoMetricDefinition[] = [
  { key: 'videoGmv', label: 'Video GMV', format: 'currency', aggregation: 'sum' },
  { key: 'videoViews', label: 'Video Views', format: 'number', aggregation: 'sum' },
  // Recomputed from totals in computeVideoMetric — see the ratio note there.
  { key: 'gpm', label: 'GPM', format: 'currency', aggregation: 'average' },
  { key: 'ctr', label: 'CTR', format: 'percent', aggregation: 'ratio', ratio: { numerator: 'productClicks', denominator: 'productImpressions' } },
  { key: 'videosPosted', label: 'Videos Posted', format: 'number', aggregation: 'sum' },
  { key: 'skuOrders', label: 'SKU Orders', format: 'number', aggregation: 'sum' },
  { key: 'shoppableVideoGmv', label: 'Shoppable Video GMV', format: 'currency', aggregation: 'sum' },
  { key: 'productImpressions', label: 'Product Impressions', format: 'number', aggregation: 'sum' },
  { key: 'productClicks', label: 'Product Clicks', format: 'number', aggregation: 'sum' },
  { key: 'videoProductViewers', label: 'Product Viewers', format: 'number', aggregation: 'sum', isDemo: true },
  { key: 'clickToOrderRate', label: 'Click → Order Rate', format: 'percent', aggregation: 'ratio', ratio: { numerator: 'skuOrders', denominator: 'productClicks' } },
  { key: 'customers', label: 'Customers', format: 'number', aggregation: 'sum' },
]

export const DEFAULT_VIDEO_KPI_METRICS: Array<VideoMetricDefinition['key']> = [
  'videoGmv',
  'videoViews',
  'gpm',
  'ctr',
  'videosPosted',
]

export function aggregateVideo(
  data: VideoDailyMetric[],
  definition: VideoMetricDefinition,
): number {
  if (data.length === 0) return 0
  if (definition.aggregation === 'ratio' && definition.ratio) {
    const { numerator, denominator } = definition.ratio
    const num = data.reduce((acc, d) => acc + ((d[numerator] as number) ?? 0), 0)
    const den = data.reduce((acc, d) => acc + ((d[denominator] as number) ?? 0), 0)
    return den > 0 ? num / den : 0
  }
  // Special-case GPM — averaging a daily rate weighs a 100-view day the same
  // as a 100k-view day. Recompute from totals so it matches GMV/views*1000.
  if (definition.key === 'gpm') {
    const totalGmv = data.reduce((acc, d) => acc + ((d.videoGmv as number) ?? 0), 0)
    const totalViews = data.reduce((acc, d) => acc + ((d.videoViews as number) ?? 0), 0)
    return totalViews > 0 ? (totalGmv / totalViews) * 1000 : 0
  }
  const values = data.map((d) => d[definition.key] as number)
  const total = values.reduce((acc, v) => acc + (v ?? 0), 0)
  return definition.aggregation === 'average' ? total / data.length : total
}

export function getVideoMetricDefinition(
  key: VideoMetricDefinition['key'],
): VideoMetricDefinition | undefined {
  return VIDEO_METRIC_CATALOG.find((m) => m.key === key)
}
